import { Button, Input, InputNumber, Modal, Radio, Space, Switch, Table, Tag, message } from 'antd';
import RenderPresetSelect from 'content/shorts-render-preset/components/RenderPresetSelect';
import RenderPreviewPane from 'content/shorts-render-preset/components/RenderPreviewPane';
import { SHORTS_LABEL_MAX_LENGTH } from 'content/shorts-render-preset/constants';
import { RenderPreviewRequest } from 'content/shorts-render-preset/types/shortsRenderPresetType';
import React, { useState } from 'react';
import {
  formatConvertErrorMessage,
  shortsConvertService,
  ShortsConvertOptions,
} from '../services/shortsConvertService';

interface AttachedSubtitle {
  id: number;
  lang: string;
}

export interface DerivedShortsRow {
  id: number;
  title: string;
  title_en?: string | null;
  slug?: string | null;
  status?: string | null;
  subtype?: string | null;
  created?: string | null;
  /** BEE-5023: 변환 진행 상태 — backend 가 primary CVA 의 video.status 를 노출. */
  video_status?: string | null;
}

interface Props {
  /** 현재 EpisodeEdit 의 content id. shorts 자체에서는 이 컴포넌트를 렌더하지 않는다. */
  contentId: number;
  /** ContentSerializer.derived_shorts — 이 content 를 원본으로 만들어진 shorts 들. */
  derivedShorts: DerivedShortsRow[];
  /** 변환 트리거 직후 부모가 derived_shorts 리스트를 새로 fetch 하도록. */
  onConverted?: (newShortsId: number) => void;
  /**
   * EpisodeEdit 가 이미 videoSubtitleService 로 가져온 primary CVA video 의 자막 목록.
   * ContentSerializer 응답에는 content_video_assets 가 없어 자체 fetch 가 빈 결과만
   * 돌려주므로 부모가 가지고 있는 데이터를 그대로 쓴다. PRD #8/#31 안내용.
   */
  primaryVideoSubtitles?: Array<{ id?: number; lang?: string | null }>;
  /**
   * 이 에피소드가 속한 시즌의 `render_preset` id.
   *
   * 미리보기 정확도 때문에 필요하다 — 실제 변환은 "요청 > 시즌 > 서버 기본값" 순으로
   * 스타일을 고르는데, **프리뷰 API 는 시즌 단계를 건너뛰고 바로 서버 기본값으로 간다.**
   * 그래서 시즌 값을 프론트에서 직접 채워 넣어야 미리보기가 결과물과 일치한다.
   *
   * 시즌 목록에서 고른 시즌은 이 필드가 없어(`SeasonListSerializer` 미포함) `undefined` 다.
   */
  seasonRenderPresetId?: number | null;
}

interface TrimModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (opts: ShortsConvertOptions) => void;
  submitting: boolean;
  attachedSubtitles: AttachedSubtitle[];
  subtitleFetchFailed: boolean;
  contentId: number;
  seasonRenderPresetId?: number | null;
}

/**
 * 라벨 하나가 가질 수 있는 세 가지 상태. 백엔드가 "필드 미전송 / 문자열 / 빈 문자열" 을
 * 서로 다르게 해석하기 때문에, 입력칸 하나로는 표현할 수 없어 모드를 따로 고른다.
 * 특히 `hide` 는 빈 입력칸이 아니라 명시적인 `''` 전송이다.
 */
type LabelMode = 'auto' | 'custom' | 'hide';

const LABEL_MODE_OPTIONS: { value: LabelMode; label: string }[] = [
  { value: 'auto', label: '자동' },
  { value: 'custom', label: '직접 입력' },
  { value: 'hide', label: '표시 안 함' },
];

/** 모드 + 입력값을 convert 옵션 한 칸으로. `undefined` 면 호출부가 필드를 뺀다. */
const labelValueFor = (mode: LabelMode, text: string): string | undefined => {
  if (mode === 'hide') return '';
  if (mode === 'custom') return text.trim();
  return undefined;
};

interface LabelFieldProps {
  title: string;
  autoHint: string;
  mode: LabelMode;
  onModeChange: (mode: LabelMode) => void;
  text: string;
  onTextChange: (text: string) => void;
  placeholder: string;
}

const LabelField: React.FC<LabelFieldProps> = ({
  title,
  autoHint,
  mode,
  onModeChange,
  text,
  onTextChange,
  placeholder,
}) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 500, minWidth: 92 }}>{title}</span>
      <Radio.Group
        size="small"
        optionType="button"
        buttonStyle="solid"
        options={LABEL_MODE_OPTIONS}
        value={mode}
        onChange={e => onModeChange(e.target.value as LabelMode)}
      />
    </div>
    {mode === 'custom' ? (
      <Input
        value={text}
        onChange={e => onTextChange(e.target.value)}
        maxLength={SHORTS_LABEL_MAX_LENGTH}
        showCount
        placeholder={placeholder}
      />
    ) : (
      <div style={{ fontSize: 12, color: '#888' }}>
        {mode === 'auto' ? autoHint : '이 라벨을 결과물에 그리지 않습니다.'}
      </div>
    )}
  </div>
);

const TrimModal: React.FC<TrimModalProps> = ({
  open,
  onClose,
  onConfirm,
  submitting,
  attachedSubtitles,
  subtitleFetchFailed,
  contentId,
  seasonRenderPresetId,
}) => {
  const [start, setStart] = useState<number | null>(null);
  const [end, setEnd] = useState<number | null>(null);
  const [burnEnabled, setBurnEnabled] = useState<boolean>(true);
  const [renderPresetId, setRenderPresetId] = useState<number | null>(null);
  const [episodeLabelMode, setEpisodeLabelMode] = useState<LabelMode>('auto');
  const [episodeLabel, setEpisodeLabel] = useState<string>('');
  const [programLabelMode, setProgramLabelMode] = useState<LabelMode>('auto');
  const [programLabel, setProgramLabel] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // PRD v1.4 Decisions #8 / #31: KO ODKR / EN Amasian. ko 우선, 없으면 en.
  const pickedLang = attachedSubtitles.find(s => s.lang.toLowerCase() === 'ko')
    ? 'ko'
    : attachedSubtitles.find(s => s.lang.toLowerCase() === 'en')
      ? 'en'
      : null;
  const hasBurnable = !!pickedLang;

  const handleOk = () => {
    if (start != null && start < 0) {
      setError('start 는 0 이상');
      return;
    }
    if (end != null && end <= 0) {
      setError('end 는 0 보다 커야 함');
      return;
    }
    if (start != null && end != null && end <= start) {
      setError('end 는 start 보다 커야 함');
      return;
    }
    // 직접 입력을 골라놓고 비워두면 서버에서는 '' = 라벨 삭제로 해석된다. 의도가
    // 갈리는 지점이라 저장 대신 되묻는다.
    if (episodeLabelMode === 'custom' && episodeLabel.trim() === '') {
      setError('상단 에피소드 라벨 문구를 입력하거나, "자동" / "표시 안 함" 을 선택하세요.');
      return;
    }
    if (programLabelMode === 'custom' && programLabel.trim() === '') {
      setError('하단 프로그램 라벨 문구를 입력하거나, "자동" / "표시 안 함" 을 선택하세요.');
      return;
    }
    setError(null);
    onConfirm({
      start_ts: start ?? undefined,
      end_ts: end ?? undefined,
      subtitle_burn: hasBurnable && !burnEnabled ? 'off' : undefined,
      render_preset_id: renderPresetId,
      label_episode: labelValueFor(episodeLabelMode, episodeLabel),
      label_program: labelValueFor(programLabelMode, programLabel),
    });
  };

  const subtitleBurnOff = hasBurnable && !burnEnabled;

  /**
   * 프리뷰 API 는 스타일을 "요청 프리셋 > 서버 기본값" 으로만 고른다 — 실제 변환과 달리
   * 시즌 단계가 없다. 셀렉트를 비워둔 채로 그냥 부르면 시즌 프리셋이 걸린 에피소드에서
   * 미리보기와 결과물이 어긋나므로, 시즌 값을 여기서 채워 넣는다.
   */
  const effectivePresetId = renderPresetId ?? seasonRenderPresetId ?? null;

  /**
   * 미리보기용 라벨 값. "직접 입력" 인데 아직 안 친 상태를 `''`(= 라벨 제거)로 보내면
   * 타이핑 도중 라벨이 사라진 그림이 나오므로, 그때만 필드를 뺀다. 저장 경로(`handleOk`)는
   * 같은 상태를 에러로 막으므로 두 곳의 규칙이 어긋나지 않는다.
   */
  const previewLabel = (mode: LabelMode, text: string): string | undefined => {
    if (mode === 'custom' && text.trim() === '') return undefined;
    return labelValueFor(mode, text);
  };

  const previewRequest: RenderPreviewRequest = {
    content_id: contentId,
    render_preset_id: effectivePresetId ?? undefined,
    label_episode: previewLabel(episodeLabelMode, episodeLabel),
    label_program: previewLabel(programLabelMode, programLabel),
    subtitle_burn: subtitleBurnOff ? 'off' : undefined,
  };

  return (
    <Modal
      title="쇼츠 변환 옵션 (trim + 자막 burn-in + 렌더 스타일)"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="변환 시작"
      cancelText="취소"
      confirmLoading={submitting}
      destroyOnClose
      width={1000}
    >
      <p style={{ marginBottom: 12, color: '#555' }}>
        Origin 영상의 임의 구간만 잘라 portrait letterbox 로 변환합니다. 두 값 모두 비우면 영상 전체
        변환.
      </p>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <Space direction="vertical" style={{ width: '100%', flex: 1, minWidth: 0 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Start (seconds)</label>
            <InputNumber
              min={0}
              step={0.5}
              value={start}
              onChange={v => setStart(v == null ? null : Number(v))}
              style={{ width: 200 }}
              placeholder="0"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>End (seconds)</label>
            <InputNumber
              min={0}
              step={0.5}
              value={end}
              onChange={v => setEnd(v == null ? null : Number(v))}
              style={{ width: 200 }}
              placeholder="(끝까지)"
            />
          </div>
          <div
            style={{
              borderTop: '1px solid #f0f0f0',
              paddingTop: 12,
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 6 }}>자막 burn-in (PRD v1.4 #8, #31)</div>
            {subtitleFetchFailed ? (
              <div style={{ fontSize: 12, color: '#cf1322' }}>
                자막 정보를 가져오지 못했습니다. 자동 burn-in 정책이 적용됩니다.
              </div>
            ) : hasBurnable ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Switch checked={burnEnabled} onChange={setBurnEnabled} size="small" />
                  <span style={{ fontSize: 13 }}>자막 burn-in (자동 선택)</span>
                </div>
                <Tag color="blue">{pickedLang?.toUpperCase()}</Tag>
                <span style={{ fontSize: 12, color: '#555' }}>
                  {' '}
                  원본에 자막이 있어 영상 하단에 자동으로 burn-in 됩니다 (ko 우선, 없으면 en).
                  끄려면 위 토글 OFF.
                </span>
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#888' }}>
                원본 비디오에 ko/en 자막이 없음 — burn 없이 letterbox 만 처리됩니다.
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>렌더 스타일</div>
            <RenderPresetSelect
              value={renderPresetId}
              onChange={setRenderPresetId}
              placeholder="시즌 기본값 사용"
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              비워두면 시즌에 연결된 렌더 프리셋을, 그것도 없으면 서버 기본값을 씁니다.
              폰트·색상·크기·비율·라벨 표시 여부가 프리셋에서 정해집니다.
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #f0f0f0',
              paddingTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontWeight: 500 }}>라벨 문구</div>
            <LabelField
              title="상단 (에피소드)"
              autoHint="에피소드 제목에서 자동으로 뽑습니다 (제목의 ':' 뒤)."
              mode={episodeLabelMode}
              onModeChange={setEpisodeLabelMode}
              text={episodeLabel}
              onTextChange={setEpisodeLabel}
              placeholder="예: 이 장면 실화냐"
            />
            <LabelField
              title="하단 (프로그램)"
              autoHint="시리즈명을 자동으로 씁니다."
              mode={programLabelMode}
              onModeChange={setProgramLabelMode}
              text={programLabel}
              onTextChange={setProgramLabel}
              placeholder="예: 오늘의 예능"
            />
            <div style={{ fontSize: 12, color: '#888' }}>
              프리셋에서 해당 라벨이 꺼져 있으면 여기서 문구를 지정해도 그려지지 않습니다.
            </div>
          </div>
          {error ? <div style={{ color: '#cf1322' }}>{error}</div> : null}
        </Space>

        <div style={{ width: 300, flexShrink: 0 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>미리보기</div>
          <RenderPreviewPane request={previewRequest} height={380} />
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            {effectivePresetId
              ? `렌더 프리셋 #${effectivePresetId}${renderPresetId ? '' : ' (시즌 기본값)'} 기준입니다.`
              : '연결된 렌더 프리셋이 없어 서버 기본 스타일로 그립니다.'}{' '}
            trim 구간과 무관하게 자막이 보이는 프레임을 서버가 골라 보여줍니다 — 배치·스타일
            확인용입니다.
          </div>
        </div>
      </div>
    </Modal>
  );
};

const VIDEO_STATUS_LABEL: Record<string, { text: string; color: string }> = {
  pending: { text: 'Pending', color: '#888' },
  transcoding: { text: 'Transcoding', color: '#1677ff' },
  uploaded: { text: 'Uploaded', color: '#1677ff' },
  completed: { text: 'Completed', color: '#52c41a' },
  failed: { text: 'Failed', color: '#cf1322' },
};

const ShortsConversionSection: React.FC<Props> = ({
  contentId,
  derivedShorts,
  onConverted,
  primaryVideoSubtitles,
  seasonRenderPresetId,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState<DerivedShortsRow[]>(derivedShorts);
  const [trimOpen, setTrimOpen] = useState(false);

  React.useEffect(() => {
    setRows(derivedShorts);
  }, [derivedShorts]);

  const attachedSubtitles: AttachedSubtitle[] = React.useMemo(
    () =>
      (primaryVideoSubtitles || [])
        .filter(s => s && s.id != null && (s.lang ?? '').length > 0)
        .map(s => ({ id: s.id as number, lang: String(s.lang).toLowerCase() })),
    [primaryVideoSubtitles]
  );
  const subtitleFetchFailed = false;

  const runConvert = async (opts: ShortsConvertOptions = {}) => {
    setSubmitting(true);
    try {
      const result = await shortsConvertService.convert(contentId, opts);
      message.success(`변환 시작됨 (shorts id: ${result.shorts_content_id})`);
      const pendingRow: DerivedShortsRow = {
        id: result.shorts_content_id,
        title: '(변환 중)',
        created: new Date().toISOString(),
        video_status: 'pending',
      };
      setRows(prev => [pendingRow, ...prev]);
      onConverted?.(result.shorts_content_id);
      setTrimOpen(false);
    } catch (e) {
      message.error(`변환 실패: ${formatConvertErrorMessage(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickConvert = () => {
    Modal.confirm({
      title: '이 영상을 쇼츠로 변환할까요?',
      content:
        '영상 전체를 시즌 기본 렌더 스타일과 자동 라벨로 변환합니다. ' +
        '구간·스타일·라벨을 지정하려면 "옵션 지정 변환" 을 사용하세요.',
      okText: '변환 시작',
      cancelText: '취소',
      onOk: () => runConvert(),
    });
  };

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>쇼츠 변환</span>
        <Button onClick={() => setTrimOpen(true)}>옵션 지정 변환</Button>
        <Button type="primary" onClick={handleQuickConvert} loading={submitting}>
          전체 변환
        </Button>
      </div>

      {rows.length === 0 ? (
        <div style={{ color: '#888', fontSize: 13 }}>
          이 영상에서 만들어진 쇼츠가 아직 없습니다.
        </div>
      ) : (
        <Table<DerivedShortsRow>
          dataSource={rows}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              width: 80,
              render: (id: number) => (
                <a href={`/episode/edit/${id}`} target="_blank" rel="noreferrer">
                  #{id}
                </a>
              ),
            },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            {
              title: 'Subtype',
              dataIndex: 'subtype',
              width: 120,
              render: (v?: string | null) => v || '-',
            },
            {
              title: 'Transcode',
              dataIndex: 'video_status',
              width: 140,
              render: (v?: string | null) => {
                if (!v) return <span style={{ color: '#888' }}>-</span>;
                const meta = VIDEO_STATUS_LABEL[v.toLowerCase()] ?? {
                  text: v,
                  color: '#888',
                };
                return <span style={{ color: meta.color }}>{meta.text}</span>;
              },
            },
            {
              title: '생성',
              dataIndex: 'created',
              width: 160,
              render: (v?: string | null) => (v ? new Date(v).toLocaleString() : '-'),
            },
          ]}
        />
      )}

      <TrimModal
        open={trimOpen}
        onClose={() => setTrimOpen(false)}
        onConfirm={opts => runConvert(opts)}
        submitting={submitting}
        attachedSubtitles={attachedSubtitles}
        subtitleFetchFailed={subtitleFetchFailed}
        contentId={contentId}
        seasonRenderPresetId={seasonRenderPresetId}
      />
    </div>
  );
};

export default ShortsConversionSection;
